import { parseDocument } from "htmlparser2";
import { Node, Text } from "slate";

import type { CustomElement, CustomText } from "./types";
import type { RichTextDocument } from "../RichText";

type HtmlNode = ReturnType<typeof parseDocument>["children"][number];
type ListElement = { type: "bulleted-list" | "numbered-list"; children: RichTextDocument };

const blockTypes = {
    p: "paragraph",
    div: "paragraph",
    h1: "heading-one",
    h2: "heading-two",
    h3: "heading-three",
    h4: "heading-four",
    h5: "heading-five",
    h6: "heading-six",
    blockquote: "citation",
    ul: "bulleted-list",
    ol: "numbered-list",
    li: "list-item",
} as const;

const markTypes = {
    strong: "bold",
    b: "bold",
    em: "italic",
    i: "italic",
    u: "underline",
    s: "strike-through",
    strike: "strike-through",
    del: "strike-through",
    code: "code",
} as const;

const safeUrl = (value: string, image = false) => {
    const protocol = value.replace(/[\u0000-\u0020\u007f]/g, "").match(/^([a-z][a-z\d+.-]*):/i)?.[1];
    return !protocol ||
        (image ? ["http", "https"] : ["http", "https", "mailto", "tel"]).includes(protocol.toLowerCase())
        ? value
        : "";
};

// Slate requires block children and inline text to live at separate levels.
const asBlocks = (nodes: RichTextDocument): RichTextDocument => {
    const blocks: RichTextDocument = [];
    const hasBlocks = nodes.some(child => !Text.isText(child));
    let text: CustomText[] = [];
    const flushText = () => {
        if (text.length && (!hasBlocks || text.some(child => child.text.trim()))) {
            blocks.push({ type: "paragraph", children: text });
        }
        text = [];
    };
    for (const node of nodes) {
        if (Text.isText(node)) {
            text.push(node);
        } else {
            flushText();
            blocks.push(node);
        }
    }
    flushText();
    return blocks;
};

const deserializeNode = (node: HtmlNode, marks: Omit<CustomText, "text"> = {}): RichTextDocument => {
    if (node.type === "text") {
        return [{ ...marks, text: node.data }];
    }
    if (
        !("attribs" in node) ||
        ["script", "style", "iframe", "object", "embed", "template", "svg", "math"].includes(node.name)
    ) {
        return [];
    }

    const tag = node.name;
    if (tag === "br") {
        return [{ ...marks, text: "\n" }];
    }
    if (tag === "img") {
        const url = safeUrl(node.attribs.src ?? "", true);
        return url ? [{ type: "image", url, children: [{ text: "" }] }] : [];
    }
    const nextMarks = { ...marks };
    if (tag in markTypes) {
        nextMarks[markTypes[tag as keyof typeof markTypes]] = true;
    }
    if (tag === "a") {
        const url = safeUrl(node.attribs.href ?? "");
        if (url) {
            nextMarks.link = url;
        }
    }
    if (tag === "ul" || tag === "ol") {
        return deserializeLists([node], nextMarks);
    }
    const children = deserializeChildren(node.children, nextMarks);
    if (tag === "pre") {
        return [{ type: "code", children: [{ text: children.map(child => Node.string(child)).join("") }] }];
    }
    if (!(tag in blockTypes)) {
        return children;
    }

    const type = blockTypes[tag as keyof typeof blockTypes];
    const hasBlocks = children.some(child => !Text.isText(child));
    if (type === "paragraph" && hasBlocks) {
        return asBlocks(children);
    }
    const normalizedChildren = hasBlocks ? asBlocks(children) : children;
    return [{ type, children: normalizedChildren.length ? normalizedChildren : [{ text: "" }] }];
};

// Quill splits mixed nested lists into adjacent ul/ol containers with ql-indent-N items.
const deserializeLists = (containers: HtmlNode[], marks: Omit<CustomText, "text">): RichTextDocument => {
    const lists: ListElement[] = [];
    const levels: { depth: number; list: ListElement }[] = [];

    for (const container of containers) {
        if (!("attribs" in container)) {
            continue;
        }
        const type = container.name === "ul" ? "bulleted-list" : "numbered-list";
        for (const node of container.children) {
            if (!("attribs" in node) || node.name !== "li") {
                continue;
            }
            const depth = Number(node.attribs.class?.match(/\bql-indent-(\d+)\b/)?.[1] ?? 0);
            while (levels.length && levels[levels.length - 1].depth > depth) {
                levels.pop();
            }
            let current = levels[levels.length - 1];
            if (current?.depth === depth && current.list.type !== type) {
                levels.pop();
                current = levels[levels.length - 1];
            }

            if (!current || current.depth < depth) {
                const parent = current?.list.children[current.list.children.length - 1];
                const previousRoot = !parent ? lists[lists.length - 1] : undefined;
                const list: ListElement = previousRoot?.type === type ? previousRoot : { type, children: [] };
                if (parent && !Text.isText(parent)) {
                    parent.children = [...asBlocks(parent.children), list];
                } else if (list !== previousRoot) {
                    lists.push(list);
                }
                current = { depth, list };
                levels.push(current);
            }
            current.list.children.push(...deserializeNode(node, marks));
        }
    }
    return lists;
};

const deserializeChildren = (nodes: HtmlNode[], marks: Omit<CustomText, "text"> = {}): RichTextDocument => {
    const children: RichTextDocument = [];
    let lists: HtmlNode[] = [];
    const flushLists = () => {
        const hasIndentation = lists.some(
            list =>
                "children" in list &&
                list.children.some(item => "attribs" in item && /\bql-indent-\d+\b/.test(item.attribs.class ?? "")),
        );
        children.push(
            ...(hasIndentation
                ? deserializeLists(lists, marks)
                : lists.flatMap(list => deserializeLists([list], marks))),
        );
        lists = [];
    };

    for (const node of nodes) {
        if ("name" in node && (node.name === "ul" || node.name === "ol")) {
            lists.push(node);
        } else if (!(lists.length && node.type === "text" && !node.data.trim())) {
            flushLists();
            children.push(...deserializeNode(node, marks));
        }
    }
    flushLists();
    return children;
};

/** Convert stored HTML directly to Slate, without interpreting literal Markdown punctuation. */
export const deserializeHtmlToRichTextDocument = (html: string): RichTextDocument => {
    const nodes = deserializeChildren(parseDocument(html).children);
    const blocks = asBlocks(nodes);
    return blocks.length ? blocks : [{ type: "paragraph", children: [{ text: "" }] }];
};

const escapeHtml = (text: string) =>
    text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

const serializeNode = (node: RichTextDocument[number]): string => {
    if (Text.isText(node)) {
        let html = escapeHtml(node.text).replace(/\n/g, "<br>");
        for (const [mark, tag] of [
            ["bold", "strong"],
            ["italic", "em"],
            ["underline", "u"],
            ["strike-through", "s"],
            ["code", "code"],
        ] as const) {
            if (node[mark]) {
                html = `<${tag}>${html}</${tag}>`;
            }
        }
        const link = node.link && safeUrl(node.link);
        return link ? `<a href="${escapeHtml(link)}">${html}</a>` : html;
    }
    if (node.type === "image") {
        const url = safeUrl(node.url, true);
        return url ? `<img src="${escapeHtml(url)}" alt="Image">` : "";
    }
    if (node.type === "code") {
        return `<pre><code>${escapeHtml(Node.string(node))}</code></pre>`;
    }
    const tags: Record<Exclude<CustomElement["type"], "image" | "code">, string> = {
        paragraph: "p",
        "heading-one": "h1",
        "heading-two": "h2",
        "heading-three": "h3",
        "heading-four": "h4",
        "heading-five": "h5",
        "heading-six": "h6",
        "block-quote": "blockquote",
        citation: "blockquote",
        "bulleted-list": "ul",
        "numbered-list": "ol",
        "list-item": "li",
    };
    const tag = tags[node.type];
    const children = node.children.map(serializeNode).join("");
    return `<${tag}>${children || (tag === "p" ? "<br>" : "")}</${tag}>`;
};

/** Produce escaped, allowlisted HTML for the existing API storage contract. */
export const serializeRichTextDocumentToHtml = (document: RichTextDocument) => document.map(serializeNode).join("");
